import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import hoistNonReactStatics from 'hoist-non-react-statics';

import {
  commandWorkspaceCohort,
  commandWorkspaceDeploymentsList,
  commandWorkspaceCohortChange,
  commandProjectActivityPublish,
  commandWorkspaceCohortAccountEnroll,
  commandWorkspaceCohortEnrollmentsList,
  commandWorkspaceCohortCollaboratorsSummary,
} from 'containers/ConnectionManager/actions';
import { selectCohort } from 'Workspace/Cohorts/store/selectors';
import cohortShape from 'Workspace/types/cohorts/cohort';

/**
 * Enhances a component with a subscription cohort, and the
 * functions required modify it.
 * @param {Function} getCohortId a function used to get the cohort id from
 *   the supplied props.
 * @param {React.Component} PlaceholderComponent An optional component that
 *   will be rendered when a cohort isn't available.
 */
export default (getCohortId, PlaceholderComponent) => (Component) => {
  class EnhancedComponent extends React.Component {
    static displayName = `EnhanceWithCohort(${Component.displayName || Component.name || 'Component'})`;

    constructor() {
      super();
      this.updateCohortPartially = this.updateCohortPartially.bind(this);
      this.getJSCohort = this.getJSCohort.bind(this);
      this.isCohortLoaded = this.isCohortLoaded.bind(this);
    }

    /**
     * Fetch collections owned by a cohort.  Ideally these should mature
     * into their own domains, but for now grab them here.
     * @param {string} cohortId
     */
    getCohortRelationships(cohortId) {
      const {
        getCohort,
        getDeployments,
        getCohortEnrollments,
        getCohortCollaboratorsSummary,
      } = this.props;

      if (cohortId) {
        getCohort(cohortId);
        getDeployments(cohortId);
        getCohortEnrollments(cohortId);
        getCohortCollaboratorsSummary(cohortId);
      }
    }

    /**
     * Get the cohort as a JS object (rather than an immutable object)
     */
    getJSCohort() {
      return this.props.cohort && this.props.cohort.toJS();
    }

    isCohortLoaded() {
      const jsCohort = this.getJSCohort();
      return !!(jsCohort && jsCohort.summary);
    }

    /**
     * The RTM will replace the entire cohort with the supplied cohort, setting any
     * field not defined to null.  This function will merge the existing cohort with
     * the supplied partial cohort, so the update doesn't destroy its existing fields. See,
     * [RTM Docs for cohort.change](https://dev-developers.smartsparrow.com/messages/workspace-cohort-change/)
     * @param {*} partialCohort
     */
    updateCohortPartially(partialCohort) {
      const { creator, workspaceId, createdAt, ...summary } = this.getJSCohort().summary;
      const { ltiConsumerCredentials } = this.getJSCohort().settings;
      const newCohort = { workspaceId, ltiConsumerCredential: ltiConsumerCredentials?.[0], ...summary, ...partialCohort };
      this.props.updateCohort(newCohort);

      const cohortId = getCohortId(this.props);
      this.getCohortRelationships(cohortId);
    }

    /**
     * On mount, Load a cohort, unless it is already in the redux store.
     */
    UNSAFE_componentWillMount() {
      // we have already loaded, don't refetch
      if (this.isCohortLoaded()) { return; }
      const cohortId = getCohortId(this.props);
      this.getCohortRelationships(cohortId);
    }

    /**
     * If the cohort Id has changed, fetch it if it is not already in
     * the store.
     */
    UNSAFE_componentWillReceiveProps(nextProps) {
      const cohortId = getCohortId(this.props);
      const nextCohortId = getCohortId(nextProps);

      if (cohortId && nextCohortId) {
        if (cohortId !== nextCohortId) {
          this.getCohortRelationships(nextCohortId);
        }
      }
    }

    render() {
      if (this.isCohortLoaded()) {
        return (
          <Component
            {...this.props}
            updateCohort={this.updateCohortPartially}
            cohort={this.getJSCohort()}
          />
        );
      }
      return (PlaceholderComponent && <PlaceholderComponent />) || null;
    }
  }

  EnhancedComponent.propTypes = {
    /* The supplied cohort object */
    cohort: cohortShape,
    /* Instructs services to fetch a cohort's enrollments.  Doesn't actually return enrollments. */
    getCohortEnrollments: PropTypes.func,
    /* Instructs services to fetch a cohort's collaborators.  Doesn't actually return collaborators. */
    getCohortCollaboratorsSummary: PropTypes.func,
    /* Instructs services to fetch a cohort.  Doesn't actually return the cohort. */
    getCohort: PropTypes.func.isRequired,
    /* Instructs services to fetch a cohort's deployments.  Doesn't actually return deployments. */
    getDeployments: PropTypes.func.isRequired,
    /* Updates the cohort (in the RTM, and in the store) with the supplied cohort */
    updateCohort: PropTypes.func.isRequired,
  };

  const mapStateToProps = (state, ownProps) => ({
    cohort: selectCohort(state, ownProps.match.params.cohortId),
  });

  const mapDispatchToProps = (dispatch) => ({
    getCohort: (cohortId) => dispatch(commandWorkspaceCohort(cohortId)),
    getDeployments: (cohortId) => dispatch(commandWorkspaceDeploymentsList(cohortId)),
    getCohortEnrollments: (cohortId) => dispatch(commandWorkspaceCohortEnrollmentsList(cohortId)),
    getCohortCollaboratorsSummary: (cohortId) => dispatch(commandWorkspaceCohortCollaboratorsSummary(cohortId)),
    updateCohort: (cohort) => dispatch(commandWorkspaceCohortChange(cohort)),
    updateDeployment: (activityId, cohortId, deploymentId, lockPluginVersionEnabled, supportOnDemandEnabled, scorable, forceUpdateAssessments) => dispatch(commandProjectActivityPublish(activityId, cohortId, deploymentId, lockPluginVersionEnabled, supportOnDemandEnabled, scorable, forceUpdateAssessments)),
    deploy: (activityId, cohortId, lockPluginVersionEnabled, supportOnDemandEnabled, scorable, forceUpdateAssessments) => dispatch(commandProjectActivityPublish(activityId, cohortId, null, lockPluginVersionEnabled, supportOnDemandEnabled, scorable, forceUpdateAssessments)),
    enrollStudent: (accountId, cohortId) => dispatch(commandWorkspaceCohortAccountEnroll(accountId, cohortId)),
  });

  const connectedComponent = connect(mapStateToProps, mapDispatchToProps)(EnhancedComponent);
  return hoistNonReactStatics(connectedComponent, Component);
};
